import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getEffectiveRole } from "utils/roleUtils";
import { getDecodedToken } from "utils/tokenUtils";
import { COMMENT_COUNT_EVENT, fetchCommentCount } from "utils/commentInbox";
import { SAFETY_SUMMARY_EVENT, fetchSafetySummary } from "utils/safetySummary";
import { buildNav } from "./navConfig";

export const useNav = () => {
  const { user } = useSelector((state) => state.user);
  const decoded = getDecodedToken();
  const role = getEffectiveRole(decoded?.role, user?.role);
  const isSystemAdmin = decoded?.designation === "SystemAdmin";
  const canReview = role === "admin" || role === "clubAdmin";
  // Content safety only shows once screening is switched on; a failed check
  // leaves it hidden.
  const [safety, setSafety] = useState(null);

  useEffect(() => {
    if (!canReview) return undefined;
    let active = true;
    const update = () =>
      fetchSafetySummary().then((summary) => {
        if (active) setSafety(summary);
      });
    update();
    window.addEventListener(SAFETY_SUMMARY_EVENT, update);
    return () => {
      active = false;
      window.removeEventListener(SAFETY_SUMMARY_EVENT, update);
    };
  }, [canReview]);
  const reviewEnabled = Boolean(safety?.enabled);
  const reviewCount = safety?.review || 0;

  const hasInbox = role === "tutor" || role === "clubAdmin";
  const [commentCount, setCommentCount] = useState(0);

  useEffect(() => {
    if (!hasInbox) return undefined;
    let active = true;
    const update = () =>
      fetchCommentCount().then((n) => {
        if (active) setCommentCount(n);
      });
    update();
    window.addEventListener(COMMENT_COUNT_EVENT, update);
    return () => {
      active = false;
      window.removeEventListener(COMMENT_COUNT_EVENT, update);
    };
  }, [hasInbox]);

  const sections = useMemo(
    () => buildNav({ role, user, reviewEnabled, reviewCount, isSystemAdmin, commentCount }),
    [role, user, reviewEnabled, reviewCount, isSystemAdmin, commentCount]
  );

  return { role, user, sections, safety: canReview ? safety : null };
};
